import React, { useState, useEffect } from 'react';
import { Send, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useGoogleSheets } from '../../hooks/useGoogleSheets';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { 
  COUNTRIES, 
  INDUSTRIES, 
  COMPANY_SIZES, 
  ALL_DM_TITLES, 
  DM_TITLES_BY_INDUSTRY, 
  QUANTITY_OPTIONS, 
  MAKE_WEBHOOK_URL 
} from '../../shared/constants';

const PlaceRequestPage: React.FC = () => {
  const { addRecord } = useGoogleSheets('lead-requests');
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [showForm, setShowForm] = useState(true);
  const [formData, setFormData] = useState({
    country: '',
    industry: '',
    employee_ranges: [] as string[],
    dm_titles: [] as string[],
    quantity_needed: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dmTitlesError, setDmTitlesError] = useState('');

  // Auto-suggest DM titles based on industry
  useEffect(() => {
    if (formData.industry && DM_TITLES_BY_INDUSTRY[formData.industry as keyof typeof DM_TITLES_BY_INDUSTRY]) {
      const suggestedTitles = DM_TITLES_BY_INDUSTRY[formData.industry as keyof typeof DM_TITLES_BY_INDUSTRY];
      setFormData(prev => ({
        ...prev,
        dm_titles: suggestedTitles
      }));
      setDmTitlesError('');
    }
  }, [formData.industry]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.dm_titles.length > 10) {
      setDmTitlesError('You can select up to 10 titles only');
      return;
    }

    setIsSubmitting(true);

    try {
      const timestamp = new Date().toISOString();
      const requestId = `REQ${String(Date.now()).slice(-5).padStart(5, '0')}`;

      // Prepare data for Google Sheets
      const newRequest = {
        timestamp,
        request_id: requestId,
        source: 'Web Form',
        country: formData.country,
        industry: formData.industry,
        employee_ranges: formData.employee_ranges.join(', '),
        dm_titles: formData.dm_titles.join(', '),
        quantity_needed: Number(formData.quantity_needed),
        requested_by: '',
        delivered_qty: 0,
        status: 'in_progress'
      };

      console.log('[PlaceRequestPage] Submitting request:', newRequest);

      // Add to Google Sheets
      await addRecord(newRequest);

      // Send webhook to make.com
      const webhookData = {
        timestamp: format(new Date(), 'MMMM d, yyyy h:mm a'),
        country: formData.country,
        industry: formData.industry,
        company_size: formData.employee_ranges.join(', '),
        dm_titles: formData.dm_titles.join(', '),
        quantity_needed: formData.quantity_needed
      };

      try {
        console.log('Sending webhook data:', webhookData);
        await fetch(MAKE_WEBHOOK_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(webhookData)
        });
        console.log('Webhook sent successfully');
      } catch (webhookError) {
        console.error('Failed to send webhook:', webhookError);
        // Don't fail the entire submission if webhook fails
      }

      // Show success screen
      setShowForm(false);

    } catch (error) {
      console.error('Failed to submit request:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleCompanySizeChange = (size: string) => {
    setFormData(prev => ({
      ...prev,
      employee_ranges: prev.employee_ranges.includes(size)
        ? prev.employee_ranges.filter(s => s !== size)
        : [...prev.employee_ranges, size]
    }));
  };

  const handleDmTitlesChange = (title: string) => {
    const newTitles = formData.dm_titles.includes(title)
      ? formData.dm_titles.filter(t => t !== title)
      : [...formData.dm_titles, title];

    if (newTitles.length > 10) {
      setDmTitlesError('You can select up to 10 titles only');
      return;
    }

    setFormData(prev => ({
      ...prev,
      dm_titles: newTitles
    }));
    setDmTitlesError('');
  };

  const isFormValid = () => {
    return formData.country && 
           formData.industry && 
           formData.employee_ranges.length > 0 && 
           formData.dm_titles.length > 0 && 
           formData.dm_titles.length <= 10 &&
           formData.quantity_needed;
  };

  const handleGoToDashboard = () => {
    navigate('/app/lead-gen/pre-qualified');
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Place Lead Request</h1>
        <p className="text-gray-600">Submit a new request for lead generation with specific targeting criteria</p>
      </div>

      {showForm ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column */}
              <div className="space-y-6">
                {/* Target Country */}
                <div>
                  <label htmlFor="country" className="block text-lg font-semibold text-gray-900 mb-2">
                    Target Country
                  </label>
                  <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                  >
                    <option value="">Select a country</option>
                    {COUNTRIES.map(country => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </select>
                </div>

                {/* Target Industry */}
                <div>
                  <label htmlFor="industry" className="block text-lg font-semibold text-gray-900 mb-2">
                    Target Industry
                  </label>
                  <select
                    id="industry"
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                  >
                    <option value="">Select one or more industries</option>
                    {INDUSTRIES.map(industry => (
                      <option key={industry} value={industry}>{industry}</option>
                    ))}
                  </select>
                </div>

                {/* Company Size */}
                <div>
                  <label className="block text-lg font-semibold text-gray-900 mb-2">
                    Company Size
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {COMPANY_SIZES.map(size => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => handleCompanySizeChange(size)}
                        className={`px-3 py-3 rounded-lg border-2 font-medium transition-all text-center ${
                          formData.employee_ranges.includes(size)
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column - Decision-Maker Titles */}
              <div>
                <div className="mb-2">
                  <div className="flex items-center justify-between">
                    <label className="text-lg font-semibold text-gray-900">
                      Decision-Maker Titles
                    </label>
                    <div className="text-sm text-blue-600">
                      (Selected: {formData.dm_titles.length}/10)
                    </div>
                  </div>
                  {dmTitlesError && (
                    <p className="text-red-600 text-sm mb-3 mt-2">{dmTitlesError}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  {ALL_DM_TITLES.map(title => (
                    <button
                      key={title}
                      type="button"
                      onClick={() => handleDmTitlesChange(title)}
                      className={`px-3 py-3 rounded-lg border-2 font-medium transition-all text-left flex items-center space-x-2 whitespace-nowrap ${
                        formData.dm_titles.includes(title)
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-base flex-shrink-0">
                        {title === 'CEO' && '👑'}
                        {title === 'CFO' && '💰'}
                        {title === 'COO' && '⚡'}
                        {title === 'CTO' && '💻'}
                        {title === 'General Manager' && '🏢'}
                        {title === 'Medical Director' && '🏥'}
                        {title === 'Procurement Director' && '📋'}
                        {title === 'Operations Director' && '⚙️'}
                        {title === 'Marketing Director' && '📊'}
                        {title === 'Finance Director' && '💳'}
                      </span>
                      <span className="text-sm">{title}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Quantity Needed */}
            <div>
              <label htmlFor="quantity_needed" className="block text-lg font-semibold text-gray-900 mb-2">
                Quantity Needed
              </label>
              <select
                id="quantity_needed"
                name="quantity_needed"
                value={formData.quantity_needed}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
              >
                <option value="">Select Quantity</option>
                {QUANTITY_OPTIONS.map(quantity => (
                  <option key={quantity} value={quantity}>{quantity}</option>
                ))}
              </select>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={isSubmitting || !isFormValid()}
                className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-3"
              >
                {isSubmitting ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <Send size={24} />
                    <span>Submit Request</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      ) : (
        /* Success Screen */
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="mb-8">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Request Submitted!</h2>
            <p className="text-xl text-gray-600 mb-8">
              Your lead request has been received.<br />
              AI Agent is now generating qualified leads for you.
            </p>
          </div>

          {/* Go to Dashboard Button */}
          <button
            onClick={handleGoToDashboard}
            className="bg-blue-600 text-white py-3 px-8 rounded-lg font-semibold text-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center space-x-2 mx-auto"
          >
            <span>Go to Leads Dashboard</span>
            <ArrowRight size={20} />
          </button>
        </div>
      )}
    </div>
  );
};

export default PlaceRequestPage;